import * as React from 'react';
import styled from 'styled-components';
import {getImageByPreview} from 'api/library';
import {WebinarObject} from 'api/webinar/types';
import BoxContainer from 'containers/BoxContainer';
import {navigateTo} from "helpers/navigate-to";

export interface WebinarPreviewProps extends WebinarObject {
  renderControls?: JSX.Element;
  renderAfterPreview?: JSX.Element;
}

const Container = styled(BoxContainer)<Pick<WebinarPreviewProps, 'live'>>`
  margin: 10px 0;
  box-sizing: border-box;
  padding: 10px;
  
  gap: 14px;
  align-items: flex-start;
  grid-template-columns: 158px 1fr max-content;
  grid-auto-flow: column;
  display: grid;
  
  background-color: ${({live, theme}) => live && theme.colors.primary};
`;

const Header = styled.div`
  gap: 10px;
  display: grid;
`;

const Preview = styled.img`
  object-fit: cover;
  border-radius: 5px;
  max-width: 158px;
  width: 100%;
  height: 88px;
`;

const Content = styled.div`
  gap: 10px;
  justify-content: flex-start;
  display: grid;
`;

const Title = styled.a`
  line-height: 20px;
  font-weight: 600;
`;

const Controls = styled.div`
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

/**
 * The component webinar preview.
 * @param {WebinarPreviewProps} props
 * @return {React.ReactElement<WebinarPreviewProps>}
 * @constructor
 */
export default function WebinarPreview(
    props: WebinarPreviewProps,
): React.ReactElement<WebinarPreviewProps> {
  /**
   * Render header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    /**
     * Render preview.
     * @return {JSX.Element}
     */
    function renderPreview(): JSX.Element {
      return (
        <Preview
          src={getImageByPreview(props.preview)}
          alt={props.title}
        />
      );
    }

    return (
      <Header>
        {renderPreview()}
        {props.renderAfterPreview}
      </Header>
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * the function do navigate to webinar view
     */
    function handleNavigate(e: any) {
      e.preventDefault();
      navigateTo('webinars.webinar.view', {id: props.id})
    }

    /**
     * Render title.
     * @return {JSX.Element}
     */
    function renderTitle(): JSX.Element {
      const link = `${process.env.PUBLIC_URL}/webinars/${props.id}/view`;

      return (
        <Title href={link} onClick={handleNavigate}>
          {props.title}
        </Title>
      );
    }

    return (
      <Content>
        {renderTitle()}
      </Content>
    );
  }

  /**
   * Render controls.
   * @return {JSX.Element | undefined}
   */
  function renderControls(): JSX.Element | undefined {
    return props.renderControls ? (
      <Controls>
        {props.renderControls}
      </Controls>
    ) : undefined;
  }

  return (
    <Container live={props.live}>
      {renderHeader()}
      {renderContent()}
      {renderControls()}
    </Container>
  );
}
